import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import PhoneInput, {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber,
} from 'react-phone-number-input'
// `react-phone-number-input` doesn't surface example numbers, so pull
// `getExampleNumber` + the compact mobile-examples dataset straight from
// `libphonenumber-js` (RPNI's own metadata source, and a direct dependency
// here) to drive a country-specific placeholder.
import { getExampleNumber } from 'libphonenumber-js'
import examples from 'libphonenumber-js/examples.mobile.json'
import { ChevronDown, Search } from 'lucide-react'
import { FieldLabel } from './Field'
// The validation helpers live in a sibling .js module so they can be
// imported from `node:test` (which doesn't load .jsx). We re-export
// them below for any consumer that wants the legacy import path.
import {
  isValidPhone,
  maxLocalDigitsForCountry,
  validatePhone,
} from './phoneValidation'
import { TIMEZONE_COUNTRY } from '../../lib/country-timezone'

export { isValidPhone, validatePhone }

// ─── Default country detection ──────────────────────────────────────
// The contact-number picker should preset the country where the user is
// opening the app — NOT a hard-coded India. We infer it from the browser:
//   1. the IANA timezone (the best signal for physical location) via a
//      compact map of common zones, then
//   2. the locale region (e.g. 'en-GB' → 'GB', 'ar-AE' → 'AE').
// Both are validated against the library's supported country list, and we
// fall back to `undefined` when we genuinely can't tell — so the picker
// shows no preset rather than a wrong country. Computed once at module
// load; a user's location doesn't change mid-session.
const SUPPORTED_COUNTRIES = new Set(getCountries())

// E.164 country calling codes are prefix-free — no code is a prefix of
// another — so a phone value equal to exactly `+<callingCode>` (e.g.
// `+246`) carries zero national digits. Picking a country before typing
// any number makes `react-phone-number-input` emit such a value; we use
// this set to recognise it as "no number entered yet" rather than invalid
// input. Any national digit makes the string longer than a bare calling
// code, so real input (even a single digit) is never mistaken for empty.
const CALLING_CODES = new Set(getCountries().map((c) => getCountryCallingCode(c)))

// Browser-timezone → country guess uses the shared TIMEZONE_COUNTRY map
// (lib/country-timezone.js) — the single source of truth also used by the
// Company country/timezone linkage in My Preferences (Region & formats).

function countryFromTimeZone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    const code = tz ? TIMEZONE_COUNTRY[tz] : undefined
    return code && SUPPORTED_COUNTRIES.has(code) ? code : undefined
  } catch {
    return undefined
  }
}

function countryFromLocale() {
  try {
    const langs =
      Array.isArray(navigator.languages) && navigator.languages.length
        ? navigator.languages
        : [navigator.language]
    for (const lang of langs) {
      if (!lang) continue
      const region = new Intl.Locale(lang).region
      if (region && SUPPORTED_COUNTRIES.has(region)) return region
    }
  } catch {
    // ignore — fall through to undefined
  }
  return undefined
}

const DETECTED_COUNTRY = countryFromTimeZone() ?? countryFromLocale()

/**
 * A real example mobile number for `country`, in international format
 * (e.g. 'AE' → "+971 50 123 4567"), used as the input placeholder so the
 * user sees the expected shape for the selected country instead of a
 * generic prompt. The international format matches what the picker renders
 * in `international` mode. Returns null for an unknown / unsupported
 * country so the caller falls back to the generic placeholder.
 */
function exampleNumberForCountry(country) {
  if (!country) return null
  try {
    const example = getExampleNumber(country, examples)
    return example ? example.formatInternational() : null
  } catch {
    return null
  }
}

/**
 * Parse `value` (E.164) and return its national digit count alongside the
 * selected country's maximum, read from the same table `validatePhone` uses
 * so the typing cap and submit validation stay in lock-step. `fallbackCountry`
 * (the picker's current country) covers values libphonenumber can't resolve a
 * country from. `max` is null for untabulated countries → treat as uncapped.
 */
function nationalDigitsAndMax(value, fallbackCountry) {
  let parsed
  try {
    parsed = parsePhoneNumber(value)
  } catch {
    // Unparseable / partial value — no reliable count; caller treats as uncapped.
  }
  const country = parsed?.country ?? fallbackCountry
  return {
    parsed,
    country,
    length: (parsed?.nationalNumber ?? '').length,
    max: maxLocalDigitsForCountry(country),
  }
}

/**
 * Truncate `value` to the selected country's maximum national length. This
 * backstops paste / programmatic input that the keystroke guard below can't
 * intercept. In-range values and untabulated countries pass through unchanged.
 */
function capToMaxLocalLength(value, fallbackCountry) {
  const { parsed, country, max } = nationalDigitsAndMax(value, fallbackCountry)
  if (!max) return value
  const callingCode =
    parsed?.countryCallingCode ?? (country ? getCountryCallingCode(country) : '')
  // National digits to measure/truncate. When the value PARSES, trust its
  // `nationalNumber`. When it does NOT — e.g. an absurdly long paste that
  // libphonenumber refuses to parse, which previously read as length 0 and
  // slipped through uncapped — fall back to the raw digits minus the
  // calling code so the paste is still truncated to the country maximum.
  let nationalDigits
  if (parsed) {
    nationalDigits = parsed.nationalNumber ?? ''
  } else {
    const digits = String(value).replace(/\D/g, '')
    nationalDigits =
      callingCode && digits.startsWith(callingCode)
        ? digits.slice(callingCode.length)
        : digits
  }
  if (nationalDigits.length <= max) return value
  return `+${callingCode}${nationalDigits.slice(0, max)}`
}

/**
 * Phone number input — wraps `react-phone-number-input` with a custom
 * country picker so the rest of the app can use a plain
 * `(value, onChange)` interface and a polished UI matching the rest
 * of the TaskMagix design.
 *
 * Wire format
 * -----------
 * - On change, `onChange(value)` fires with either:
 *     - an E.164 string (e.g. `'+919876543210'`) once the user has
 *       typed a valid number for the picked country, OR
 *     - `''` (empty string) when the field is empty.
 * - On mount, an existing E.164 value is parsed and the picker
 *   auto-selects the matching flag.
 *
 * Validation
 * ----------
 * Use `isValidPhone(value)` (re-exported below) inside a Zod
 * `.refine()` or a manual submit gate. Empty / undefined values
 * return `true` so the helper is safe for optional fields; callers
 * pre-check "required" themselves.
 */
export function PhoneInputField({
  label,
  value,
  onChange,
  error,
  required = false,
  // Defaults to the country the user is opening the app from (browser
  // timezone / locale), NOT a hard-coded India. Callers can still pass an
  // explicit `defaultCountry` to override.
  defaultCountry = DETECTED_COUNTRY,
  disabled = false,
  // Omitted by default so the field can show a country-specific example
  // number (see `effectivePlaceholder`). A caller that passes an explicit
  // placeholder still wins.
  placeholder,
  id,
  helperText,
  // Two-way country sync (opt-in). When `country` (an ISO alpha-2 code) is
  // supplied, the phone's country FOLLOWS it — so a sibling Country field
  // can drive the flag. `onCountryChange(code)` fires when the user changes
  // the phone's OWN country (flag pick, or typing an international number),
  // so that sibling field can follow back. Both optional; callers that omit
  // them get the previous uncontrolled behaviour unchanged.
  country,
  onCountryChange,
}) {
  const inputId = id ?? 'phone-input'

  // The inner RPNI <PhoneInput> is uncontrolled for country (it only accepts
  // `defaultCountry`), so to adopt a country pushed in from the `country`
  // prop we remount it via a changing key. We ONLY remount when the incoming
  // country differs from what the phone itself last reported — a country the
  // phone chose (flag pick / typed international number) must never trigger a
  // disruptive mid-typing remount.
  const [remountKey, setRemountKey] = useState(0)
  // Current picker country, tracked purely to drive the example placeholder.
  // Seeded from the controlled `country` or the detected default; kept in
  // sync by `handleCountryChange` (which RPNI fires on mount and on every
  // flag change) and the sibling-country effect below.
  const [activeCountry, setActiveCountry] = useState(() => country || defaultCountry)
  const lastPhoneCountryRef = useRef(country ?? null)
  const readyRef = useRef(false)
  useEffect(() => {
    readyRef.current = true
  }, [])
  useEffect(() => {
    if (!country) return
    if (country !== lastPhoneCountryRef.current) {
      lastPhoneCountryRef.current = country
      setActiveCountry(country)
      // Adopt the sibling country cleanly. RPNI keeps a value's OWN country over
      // `defaultCountry`, so a lingering number keeps the old flag + `+code` and
      // the field appears to ignore the Country pick (and a `+1` number can even
      // re-detect a different NANP country). Reset the value so the remount
      // below shows the newly-chosen country's flag + dial code; the user then
      // types the number for that country.
      if (typeof onChange === 'function' && value) onChange('')
      setRemountKey((k) => k + 1)
    }
    // `value` / `onChange` are read intentionally without subscribing — the
    // reset must run ONLY when the sibling country changes, not per keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country])

  const handleCountryChange = (next) => {
    lastPhoneCountryRef.current = next ?? null
    // Keep the example placeholder in step with the picked country. Runs even
    // for the mount-time auto-detect (unlike the sibling-sync below), so the
    // placeholder is correct from first paint.
    setActiveCountry(next || undefined)
    // Swallow the mount-time auto-detect so opening the form doesn't pre-fill
    // a sibling Country field with the browser guess — sync only on a real
    // user change.
    if (!readyRef.current) return
    if (typeof onCountryChange === 'function' && next) onCountryChange(next)
  }

  const handleChange = (next) => {
    // Selecting a country before typing any digits emits a dial-code-only
    // value (e.g. `+246`). Treat that as empty so it can't trigger the
    // "Please enter a valid Contact Number." validation before the user
    // has typed anything; the moment a national digit is added the value
    // is longer than a bare calling code and is passed through unchanged.
    let value = next ?? ''
    if (value && CALLING_CODES.has(value.slice(1))) value = ''
    // Backstop for paste / programmatic input: truncate anything past the
    // country's maximum national length. Typed digits are blocked earlier by
    // `handleKeyDown`; the number is still fully revalidated on submit.
    if (value) value = capToMaxLocalLength(value, activeCountry)
    if (typeof onChange === 'function') onChange(value)
  }

  // Stop accepting further digits once the selected country's maximum
  // national length is reached, so an impossible number (e.g. 21 digits)
  // can't be typed. We block at the keystroke — before react-phone-number-
  // input sees it — because RPNI keeps its own input state and won't revert
  // a value we reject after the fact. Editing keys, shortcuts, and replacing
  // a selection stay unaffected; untabulated countries are left uncapped.
  const handleKeyDown = (event) => {
    // Let shortcuts (copy / paste / select-all) and editing / navigation keys
    // (Backspace, Delete, arrows, Tab…) through untouched — those carry a
    // modifier or have a multi-character `event.key` name.
    if (event.ctrlKey || event.metaKey || event.altKey) return
    if (event.key.length !== 1) return
    // A single printable character. Only digits are valid input — block
    // letters and punctuation outright so a stray keystroke can't clear or
    // re-parse the existing number. Without this, typing a letter over a
    // selected number makes react-phone-number-input drop the whole value to
    // just the calling code (and sometimes switch country). The key never
    // reaches RPNI, so the saved number and country stay put.
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault()
      return
    }
    // A digit — enforce the per-country maximum length. Replacing a selection
    // can't grow the number past the cap, so allow it.
    const el = event.currentTarget
    if (el.selectionStart !== el.selectionEnd) return
    const { length, max } = nationalDigitsAndMax(value, activeCountry)
    if (max && length >= max) event.preventDefault()
  }

  // When the caller hands us an existing E.164 string sniff the
  // country off it so the picker doesn't drop back to `defaultCountry`
  // on edit and look wrong.
  const initialCountry = useMemo(() => {
    if (typeof value === 'string' && value.length > 0) {
      try {
        const parsed = parsePhoneNumber(value)
        if (parsed?.country) return parsed.country
      } catch {
        // Fall through — bad / partial value, stick with default.
      }
    }
    return defaultCountry
  }, [value, defaultCountry])

  // An explicit caller placeholder wins; otherwise show a country-specific
  // example number, falling back to the generic prompt when the country is
  // unknown / unsupported.
  const effectivePlaceholder = useMemo(() => {
    if (placeholder != null) return placeholder
    return exampleNumberForCountry(activeCountry) ?? 'Enter contact number'
  }, [placeholder, activeCountry])

  const borderClass = error
    ? 'border-[color:var(--color-priority-urgent)]'
    : 'border-[color:var(--color-rule)]'

  return (
    <div>
      <FieldLabel label={label} required={required} htmlFor={inputId} />
      <div
        className={
          // Wrapper mimics the `TextField` shell so the country
          // picker + input feel like one component. Focus-within is
          // used so focusing either half lights up the same border.
          'flex h-10 rounded-lg border bg-[color:var(--color-card)] ' +
          'transition-colors focus-within:border-[color:var(--color-accent)] ' +
          borderClass +
          (disabled ? ' opacity-60 pointer-events-none' : '')
        }
      >
        <PhoneInput
          // Remount to adopt a country pushed in via the `country` prop
          // (see the sync machinery above). The key only changes on a
          // sibling-driven country change, never on phone-driven ones.
          key={remountKey}
          id={inputId}
          placeholder={effectivePlaceholder}
          value={value || undefined}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onCountryChange={handleCountryChange}
          // When a sibling `country` is supplied it wins as the initial
          // country on (re)mount; otherwise fall back to the value-sniffed
          // or browser-detected default.
          defaultCountry={country || initialCountry}
          international
          countryCallingCodeEditable={false}
          disabled={disabled}
          // Custom searchable dropdown — replaces the library's
          // native <select> with a popover that matches the
          // mock-up: flag-only trigger + search-enabled list.
          countrySelectComponent={CountrySelect}
          addInternationalOption={false}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${inputId}-error` : undefined}
          className="taskmagix-phone-input w-full"
        />
      </div>
      {helperText && !error ? (
        <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
          {helperText}
        </p>
      ) : null}
      {error ? (
        <p
          id={`${inputId}-error`}
          role="alert"
          className="mt-1 text-xs text-[color:var(--color-priority-urgent)]"
        >
          {error}
        </p>
      ) : null}
    </div>
  )
}

/**
 * Drop-in replacement for the library's default country `<select>`.
 *
 * Renders:
 *   - A trigger button showing only the current country's flag + a
 *     caret. The dial code stays inside the phone input (rendered by
 *     RPNI's `international` mode), keeping the trigger narrow.
 *   - A popover panel with:
 *       - A search box (filters by country name OR dial code).
 *       - A scrollable list of `flag · name · +dialCode`. The
 *         currently-selected country is highlighted.
 *
 * Props are the contract RPNI hands to `countrySelectComponent`:
 *   value          ISO 3166-1 alpha-2 country code, e.g. 'IN'
 *   onChange(next) Called when the user picks a country
 *   options        Array of { value, label } pairs RPNI computes
 *                  (we drop the international / divider entries)
 *   iconComponent  Flag component RPNI passes in — render it as the
 *                  trigger glyph + each list-row glyph so the visual
 *                  language stays in sync with the rest of RPNI.
 */
function CountrySelect({
  value,
  onChange,
  options,
  iconComponent: IconComponent,
  disabled,
  readOnly,
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef(null)
  const searchRef = useRef(null)
  const panelRef = useRef(null)
  // Viewport rect of the whole field — anchors the PORTALED panel.
  const [rect, setRect] = useState(null)
  // Keyboard navigation — which row is highlighted, plus refs to each row
  // so the highlighted one can be scrolled into view as the user arrows.
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const optionRefs = useRef([])

  // Filter + sort: real countries only (drop the "International" /
  // divider entries RPNI appends), alphabetical by name.
  const filteredOptions = useMemo(() => {
    const real = options.filter((o) => o && o.value && !o.divider)
    const sorted = [...real].sort((a, b) =>
      (a.label || '').localeCompare(b.label || ''),
    )
    const q = query.trim().toLowerCase()
    if (!q) return sorted
    return sorted.filter((o) => {
      let dial = ''
      try {
        dial = getCountryCallingCode(o.value)
      } catch {
        // Some entries don't have a calling code — skip dial match
      }
      return (
        (o.label || '').toLowerCase().includes(q) ||
        String(dial).includes(q.replace(/^\+/, ''))
      )
    })
  }, [options, query])

  // Auto-focus the search box once the panel mounts. The reset on
  // close lives in `closePanel()` below so we don't trigger the
  // react-hooks/set-state-in-effect lint rule.
  useEffect(() => {
    if (!open) return undefined
    const t = setTimeout(() => searchRef.current?.focus(), 0)
    return () => clearTimeout(t)
  }, [open])

  // Keep the highlighted row scrolled into view during arrow navigation.
  useEffect(() => {
    if (!open) return
    optionRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' })
  }, [highlightedIndex, open])

  const closePanel = () => {
    setOpen(false)
    setQuery('')
  }

  // Portal anchoring — track the field's viewport rect so the
  // fixed-position panel stays glued to it across scroll / resize.
  // The panel used to be `absolute` INSIDE the field wrapper, but an
  // absolutely-positioned child still inflates its scroll container's
  // scrollable area — so opening the picker inside a Modal (Create
  // Company, member dialogs, Complete Profile) grew the modal body's
  // scrollHeight and visibly disrupted the dialog layout
  // (QA-reported). Portaling to <body> takes the panel out of every
  // ancestor's flow entirely. Mirrors ui/CountryFilter's portal mode.
  const reposition = useCallback(() => {
    const el = containerRef.current
    if (el) setRect(el.getBoundingClientRect())
  }, [])
  useEffect(() => {
    if (!open) return undefined
    reposition()
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [open, reposition])

  // Outside-click + Escape close the popover. Pointerdown rather than
  // click so the popover dismisses before the next click registers
  // on whatever was clicked outside (matches Modal / Select behaviour).
  // The panel lives in a portal, so BOTH refs must be checked, and
  // Escape is handled in the CAPTURE phase with stopPropagation so
  // closing the popover doesn't also close a surrounding Modal on the
  // same keystroke (same nuance as ui/CountryFilter).
  useEffect(() => {
    if (!open) return undefined
    function onPointerDown(e) {
      const t = e.target
      if (
        !containerRef.current?.contains(t) &&
        !panelRef.current?.contains(t)
      ) {
        closePanel()
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') {
        e.stopPropagation()
        closePanel()
      }
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [open])

  function pick(country) {
    onChange(country)
    setOpen(false)
  }

  // Arrow keys move the highlight (scrolled into view by the effect above);
  // Enter selects the highlighted country. Bound to the search input since
  // it holds focus while the panel is open.
  function onSearchKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((i) => Math.min(i + 1, filteredOptions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const opt = filteredOptions[highlightedIndex]
      if (opt) pick(opt.value)
    }
  }

  const triggerDisabled = disabled || readOnly

  return (
    <div ref={containerRef} className="relative flex items-center">
      <button
        type="button"
        onClick={() => {
          if (triggerDisabled) return
          if (!open) {
            // Seed the keyboard highlight to the current selection so arrow
            // nav starts there (query is empty on open → full list).
            const idx = filteredOptions.findIndex((o) => o.value === value)
            setHighlightedIndex(idx >= 0 ? idx : 0)
          }
          setOpen((o) => !o)
        }}
        disabled={triggerDisabled}
        aria-label="Select country"
        aria-haspopup="listbox"
        aria-expanded={open}
        className={
          'flex items-center gap-1 h-full px-2 rounded-l-lg ' +
          'cursor-pointer hover:bg-[color:var(--color-card-hover)] ' +
          'focus:outline-none focus-visible:bg-[color:var(--color-card-hover)] ' +
          (triggerDisabled ? 'cursor-not-allowed' : '')
        }
      >
        {value ? (
          <IconComponent country={value} label={value} aspectRatio={1.5} />
        ) : (
          <span
            aria-hidden="true"
            className="h-4 w-6 rounded-sm bg-[color:var(--color-canvas)] border border-[color:var(--color-rule)]"
          />
        )}
        <ChevronDown
          size={12}
          aria-hidden="true"
          className={
            'shrink-0 text-[color:var(--color-ink-muted)] transition-transform ' +
            (open ? 'rotate-180' : '')
          }
        />
      </button>

      {open && rect
        ? createPortal(
        <div
          ref={panelRef}
          role="listbox"
          // Fixed-position portal panel anchored to the field's rect —
          // flips upward when there isn't room below, height capped to
          // the viewport. Same math as ui/CountryFilter's portal mode.
          style={(() => {
            const GAP = 4
            const MARGIN = 8
            const width = Math.max(rect.width, 256)
            const spaceBelow = window.innerHeight - rect.bottom - MARGIN
            const spaceAbove = rect.top - MARGIN
            const openUp = spaceBelow < 240 && spaceAbove > spaceBelow
            const s = {
              position: 'fixed',
              left: Math.max(
                MARGIN,
                Math.min(rect.left, window.innerWidth - width - MARGIN),
              ),
              width,
              maxHeight: Math.max(
                160,
                Math.min(360, openUp ? spaceAbove : spaceBelow),
              ),
              zIndex: 1000,
            }
            if (openUp) s.bottom = window.innerHeight - rect.top + GAP
            else s.top = rect.bottom + GAP
            return s
          })()}
          className={
            'flex flex-col rounded-md border border-[color:var(--color-rule)] ' +
            'bg-[color:var(--color-card)] shadow-lg overflow-hidden'
          }
        >
          <div className="p-2 border-b border-[color:var(--color-rule)] shrink-0">
            <div className="relative">
              <Search
                size={14}
                aria-hidden="true"
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-muted)]"
              />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value)
                  // Reset the highlight to the top match as the list narrows.
                  setHighlightedIndex(0)
                }}
                onKeyDown={onSearchKeyDown}
                placeholder="Search Country..."
                className={
                  'w-full h-9 pl-8 pr-3 rounded-md border ' +
                  'border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] ' +
                  'text-sm text-[color:var(--color-ink)] ' +
                  'placeholder:text-[color:var(--color-ink-subtle)] ' +
                  'outline-none focus:border-[color:var(--color-accent)]'
                }
              />
            </div>
          </div>
          <div className="min-h-0 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-sm text-center text-[color:var(--color-ink-muted)]">
                No countries found.
              </div>
            ) : (
              filteredOptions.map((opt, i) => {
                const isSelected = opt.value === value
                let dial = ''
                try {
                  dial = getCountryCallingCode(opt.value)
                } catch {
                  // No-op — list row just won't show a dial code.
                }
                return (
                  <button
                    key={opt.value}
                    ref={(el) => {
                      optionRefs.current[i] = el
                    }}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => pick(opt.value)}
                    onMouseEnter={() => setHighlightedIndex(i)}
                    className={
                      'w-full flex items-center gap-3 px-3 py-2 text-sm text-left ' +
                      'cursor-pointer hover:bg-[color:var(--color-card-hover)] ' +
                      (i === highlightedIndex
                        ? 'bg-[color:var(--color-accent-bg)] ring-1 ring-inset ring-[color:var(--color-accent)]'
                        : isSelected
                          ? 'font-medium'
                          : '')
                    }
                  >
                    <IconComponent
                      country={opt.value}
                      label={opt.label}
                      aspectRatio={1.5}
                    />
                    <span className="flex-1 truncate text-[color:var(--color-ink)]">
                      {opt.label}
                    </span>
                    {dial ? (
                      <span className="text-xs tabular-nums text-[color:var(--color-ink-muted)]">
                        +{dial}
                      </span>
                    ) : null}
                  </button>
                )
              })
            )}
          </div>
        </div>,
        document.body,
        )
        : null}
    </div>
  )
}

